import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { PublicKey, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { submitOrder, checkExistingOrder } from '../supabaseClient';
import { lookupZipCode } from '../utils/zipCodeLookup';
import { 
  buildPurchaseInstruction, 
  createTransaction, 
  getTaxEscrowPDA,
  getPopcatEscrowPDA,
  getPoppersEscrowPDA,
  generateOrderId,
  WALLETS,
  SEEKER_WALLETS 
} from '../utils/popperverseProgram';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  
  // Get order data from navigation state
  const orderData = location.state;
  const { product, walletAddress, basePrice } = orderData || {};

  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: 'United States'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [transactionSignature, setTransactionSignature] = useState(null);
  const [shippingCost, setShippingCost] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  
  // Fresh SOL price - fetched on page load to avoid slippage
  const [currentSolPrice, setCurrentSolPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);

  // Fetch fresh SOL price on page load to avoid stale price slippage
  useEffect(() => {
    const fetchSolPrice = async () => {
      setPriceLoading(true);
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd');
        const data = await response.json();
        setCurrentSolPrice(data.solana.usd);
        console.log('[Checkout] Fresh SOL price fetched:', data.solana.usd);
      } catch (error) {
        console.error('[Checkout] Error fetching SOL price:', error);
        // Fallback to passed price or default
        setCurrentSolPrice(orderData?.solPriceUSD || 150);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchSolPrice();
    // Refresh every 30 seconds while on page
    const interval = setInterval(fetchSolPrice, 30000);
    return () => clearInterval(interval);
  }, [orderData?.solPriceUSD]);

  // Redirect if no order data
  useEffect(() => {
    if (!product || !walletAddress || !basePrice) {
      navigate('/shop');
    }
  }, [product, walletAddress, basePrice, navigate]);

  // Calculate shipping based on country
  useEffect(() => {
    if (formData.country) {
      const isUSA = formData.country === 'United States' || formData.country === 'USA';
      setShippingCost(isUSA ? 10 : 25);
    }
  }, [formData.country]);

  // Calculate tax for US orders
  useEffect(() => {
    if (!basePrice || basePrice <= 0) {
      setTaxAmount(0);
      return;
    }
    
    if (formData.country === 'United States' && formData.stateProvince) {
      const taxRate = 0.07;
      const safeBase = Number(basePrice) || 0;
      const safeShipping = Number(shippingCost) || 0;
      setTaxAmount((safeBase + safeShipping) * taxRate);
    } else {
      setTaxAmount(0);
    }
  }, [formData.country, formData.stateProvince, basePrice, shippingCost]);

  const calculateTotal = () => {
    const safeBasePrice = Number(basePrice) || 0;
    const safeShipping = Number(shippingCost) || 0;
    const safeTax = Number(taxAmount) || 0;
    const totalUSD = safeBasePrice + safeShipping + safeTax;
    // Use fresh SOL price instead of stale navigation state
    const safeSolPrice = Number(currentSolPrice) || 150;
    const totalSOL = safeSolPrice > 0 ? (totalUSD / safeSolPrice).toFixed(4) : '0.0000';
    return { usd: totalUSD.toFixed(2), sol: totalSOL };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // Auto-fill city/state when zip code is entered (US only)
    if (name === 'postalCode' && value.length === 5 && formData.country === 'United States') {
      const zipData = lookupZipCode(value);
      if (zipData) {
        setFormData(prev => ({
          ...prev,
          postalCode: value,
          city: zipData.city || prev.city,
          stateProvince: zipData.stateCode || prev.stateProvince
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate required fields
    if (!formData.customerName || !formData.email || !formData.addressLine1 || 
        !formData.city || !formData.postalCode || !formData.country) {
      setError('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    try {
      // Check if user already ordered this product
      const { exists } = await checkExistingOrder(walletAddress, product.id);
      if (exists) {
        setError('You have already pre-ordered this product!');
        setIsSubmitting(false);
        return;
      }

      // Calculate final total and SOL amount
      const total = calculateTotal();
      const solAmount = parseFloat(total.sol);
      const subtotalLamports = Math.floor(solAmount * LAMPORTS_PER_SOL);
      
      // Calculate distribution amounts based on Popperverse tokenomics
      // Tax: from taxAmount state (already calculated)
      // Use fresh currentSolPrice instead of stale solPriceUSD
      const taxLamports = Math.floor((taxAmount / currentSolPrice) * LAMPORTS_PER_SOL);
      
      // Post-tax subtotal for royalty/donation calculations
      const postTaxSubtotal = subtotalLamports - taxLamports;
      
      // 5% royalty total (split on-chain: 1% creator + 4% holder)
      const royaltyLamports = Math.floor(postTaxSubtotal * 0.05);
      
      // 5% donation
      const donationLamports = Math.floor(postTaxSubtotal * 0.05);

      // Derive escrow PDAs
      const taxEscrow = await getTaxEscrowPDA();
      const popcatEscrow = await getPopcatEscrowPDA();
      const poppersEscrow = await getPoppersEscrowPDA();

      // Generate order ID and product code
      const orderId = generateOrderId();
      // Product code must be <= 20 chars. Use code if available, otherwise truncate id
      let productCode = product.code || `POP-${(product.id || '0').toString().slice(0, 15)}`;
      productCode = productCode.slice(0, 20); // Ensure max 20 chars

      // Get NFT mint address (required for on-chain royalty validation)
      // product.id is the NFT mint address, or use product.nftMint if available
      const nftMint = new PublicKey(product.nftMint || product.id);
      console.log('[Checkout] NFT Mint:', nftMint.toBase58());

      // Get creator wallet (original designer - receives 1% permanent royalty)
      const creatorWallet = product.creatorWallet 
        ? new PublicKey(product.creatorWallet) 
        : WALLETS.DEFAULT_CREATOR;

      // Get NFT holder wallet (current NFT owner - receives 4% royalty)
      // If not set, default to creator (before NFT is sold, creator is also holder)
      const nftHolderWallet = product.nftHolderWallet 
        ? new PublicKey(product.nftHolderWallet) 
        : creatorWallet;

      // Build the purchase instruction using the Popperverse program
      // PurchaseV2 uses SEEKER (cold storage) wallets for treasury/donate
      const purchaseIx = buildPurchaseInstruction({
        buyer: publicKey,
        nftMint,
        creatorWallet,
        nftHolderWallet,
        seekerTreasury: SEEKER_WALLETS.TREASURY,
        seekerDonate: SEEKER_WALLETS.DONATE,
        taxEscrow,
        popcatEscrow,
        poppersEscrow,
        orderId,
        productCode,
        subtotalLamports,
        taxLamports,
        royaltyLamports,
        donationLamports,
      });

      // Create transaction
      const transaction = createTransaction(purchaseIx);

      // Get latest blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      // Simulate transaction first to get detailed error info
      console.log('[Checkout] Simulating transaction...');
      try {
        const simulation = await connection.simulateTransaction(transaction);
        if (simulation.value.err) {
          console.error('[Checkout] Simulation error:', simulation.value.err);
          console.error('[Checkout] Simulation logs:', simulation.value.logs);
          throw new Error(`Transaction simulation failed: ${JSON.stringify(simulation.value.err)}\nLogs: ${simulation.value.logs?.join('\n')}`);
        }
        console.log('[Checkout] Simulation passed ✓');
      } catch (simError) {
        console.error('[Checkout] Simulation exception:', simError);
        throw simError;
      }

      // Send transaction
      const signature = await sendTransaction(transaction, connection);
      await connection.confirmTransaction(signature, 'confirmed');
      
      setTransactionSignature(signature);

      // Prepare order data with all details
      const orderSubmitData = {
        wallet_address: walletAddress,
        product_name: product.name,
        product_id: product.id,
        sol_amount: total.sol,
        transaction_signature: signature,
        customer_name: formData.customerName,
        email: formData.email,
        phone: formData.phone || null,
        address_line1: formData.addressLine1,
        address_line2: formData.addressLine2 || null,
        city: formData.city,
        state_province: formData.stateProvince,
        postal_code: formData.postalCode,
        country: formData.country,
        order_status: 'pending',
        shipping_cost: shippingCost,
        tax_amount: parseFloat(taxAmount.toFixed(2)),
        total_usd: parseFloat(total.usd)
      };

      // Submit to Supabase
      const result = await submitOrder(orderSubmitData);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/shop');
        }, 3000);
      } else {
        setError(result.error || 'Failed to submit order. Please try again.');
      }
    } catch (error) {
      console.error('[Checkout] Order submission failed:', error);
      // Extract more detailed error info if available
      if (error.logs) {
        console.error('[Checkout] Transaction logs:', error.logs);
      }
      if (error.error) {
        console.error('[Checkout] Inner error:', error.error);
      }
      setError(error.message || 'Transaction failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!product || !walletAddress || !basePrice || !currentSolPrice) {
    return null;
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        {success ? (
          <div className="success-message">
            <h2>✅ Order Submitted!</h2>
            <p>Thank you for your purchase. We'll process your order shortly.</p>
            <p className="success-details">Order confirmation sent to {formData.email}</p>
            <p className="redirect-note">Redirecting to shop...</p>
          </div>
        ) : (
          <>
            <div className="checkout-header">
              <button className="back-btn" onClick={() => navigate('/shop')}>
                ← Back to Shop
              </button>
              <h2>Shipping Information</h2>
              <p className="checkout-subtitle">Complete your order for {product.name}</p>
            </div>
            
            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit} className="checkout-form">
              <div className="form-group">
                <label htmlFor="customerName">Full Name *</label>
                <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="country">Country *</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Japan">Japan</option>
                    <option value="Mexico">Mexico</option>
                    <option value="Brazil">Brazil</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode">Postal/Zip Code *</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    required
                    placeholder="10001"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="New York"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="stateProvince">State/Province</label>
                  <input
                    type="text"
                    id="stateProvince"
                    name="stateProvince"
                    value={formData.stateProvince}
                    onChange={handleChange}
                    placeholder="NY"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="addressLine1">Address Line 1 *</label>
                <input
                  type="text"
                  id="addressLine1"
                  name="addressLine1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  required
                  placeholder="123 Main Street"
                />
              </div>

              <div className="form-group">
                <label htmlFor="addressLine2">Address Line 2</label>
                <input
                  type="text"
                  id="addressLine2"
                  name="addressLine2"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  placeholder="Apt 4B"
                />
              </div>

              <div className="order-summary">
                <h3>Order Summary</h3>
                <p><strong>Product:</strong> {product?.name || 'Product'}</p>
                <p><strong>Base Price:</strong> ${(basePrice || 0).toFixed(2)} USD</p>
                <p><strong>Shipping:</strong> ${(shippingCost || 0).toFixed(2)} USD</p>
                {taxAmount > 0 && <p><strong>Tax:</strong> ${(taxAmount || 0).toFixed(2)} USD</p>}
                <p className="total-line"><strong>Total:</strong> ${calculateTotal().usd} USD ({priceLoading ? '...' : calculateTotal().sol} SOL)</p>
                <p className="sol-rate"><small>SOL Rate: ${currentSolPrice?.toFixed(2) || '...'} USD (live)</small></p>
                <p className="wallet-info"><strong>Wallet:</strong> {walletAddress?.slice(0, 4)}...{walletAddress?.slice(-4)}</p>
              </div>

              <button 
                type="submit" 
                className="submit-order-btn"
                disabled={isSubmitting || priceLoading}
              >
                {isSubmitting ? 'Processing Payment...' : priceLoading ? 'Loading Price...' : `Pay ${calculateTotal().sol} SOL`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Checkout;
